/**
 * Customer review 11 Sep (V11): the supplier's approval of record — level,
 * date and approver — as a summary card at the top of the Approvals tab.
 * The header badge shows the level alone; this is where the "when" and
 * "by whom" live. Every field is optional: a supplier that has not been
 * approved yet, or was approved before the approver was recorded, still gets
 * a truthful card rather than blanks.
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Award, CalendarCheck, UserCheck } from 'lucide-react';
import { useFormatters } from '../../contexts/PreferencesContext';

export interface ApprovalSummary {
  approval_level?: '1' | '2' | '3' | null;
  approval_level_display?: string | null;
  approved_at?: string | null;
  approved_by_name?: string | null;
}

interface ApprovalStatusCardProps {
  approval: ApprovalSummary;
}

export function ApprovalStatusCard({ approval }: ApprovalStatusCardProps) {
  const { formatDate } = useFormatters();
  const approved = Boolean(approval.approved_at || approval.approval_level);
  const levelLabel = approval.approval_level_display
    || (approval.approval_level ? `Level ${approval.approval_level}` : null);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Award className="h-5 w-5" />
          Approval status
          {approved ? (
            <Badge variant="outline" className="ml-2 border-green-300 text-green-800 dark:text-green-200">
              Approved
            </Badge>
          ) : (
            <Badge variant="outline" className="ml-2 text-muted-foreground">
              Not yet approved
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {approved ? (
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <Award className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <dt className="text-muted-foreground">Level</dt>
                <dd className="font-medium">{levelLabel ?? 'Not recorded'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CalendarCheck className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <dt className="text-muted-foreground">Approved on</dt>
                <dd className="font-medium">
                  {approval.approved_at ? formatDate(approval.approved_at) : 'Not recorded'}
                </dd>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <UserCheck className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <dt className="text-muted-foreground">Approved by</dt>
                <dd className="font-medium">{approval.approved_by_name || 'Not recorded'}</dd>
              </div>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-muted-foreground">
            This supplier has not been approved yet. The level, date and approver are recorded
            when the onboarding approval is granted.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
